import { z } from "zod";

/** The kinds of extension version an organisation library can hold. */
export const libraryItemKinds = ["activity", "skill", "pack"] as const;

/**
 * The states a version in an organisation library reports. `submitted` and `evaluating` are
 * still in review; `published` is approved and installable; `withdrawn` is revoked.
 */
export const libraryItemStates = [
  "submitted",
  "evaluating",
  "approved",
  "rejected",
  "published",
  "withdrawn",
] as const;

/** One reviewed pack, skill, or activity in an organisation's private library. */
export const organisationLibraryItemSchema = z.object({
  id: z.uuid(),
  kind: z.enum(libraryItemKinds),
  name: z.string().min(1).max(120),
  version: z.string().min(1).max(80),
  digest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  state: z.enum(libraryItemStates),
});
export type OrganisationLibraryItem = z.infer<typeof organisationLibraryItemSchema>;

/** One page of an organisation's private library. A null cursor means it is the last page. */
export const organisationLibrarySchema = z.object({
  items: z.array(organisationLibraryItemSchema),
  nextCursor: z.string().min(1).max(1000).nullable(),
});
export type OrganisationLibrary = z.infer<typeof organisationLibrarySchema>;

/**
 * One published extension version in the catalogue (MK9). It mirrors the hub's ExtensionVersion
 * without the manifest reference, which stays on the server. The hub reports the declared status
 * and domain for packs imported from the catalogue; a version published through the marketplace
 * declares neither and reports null for both.
 */
export const extensionCatalogueItemSchema = z.object({
  id: z.uuid(),
  kind: z.enum(libraryItemKinds),
  name: z.string().min(1).max(120),
  version: z.string().min(1).max(80),
  digest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  state: z.enum(libraryItemStates),
  capabilities: z.array(z.string().min(1).max(256)).max(30),
  // The status and domain the catalogue declared at import. Null for a version
  // published through the marketplace: it declared neither.
  packStatus: z.enum(["trial", "enabled", "disabled"]).nullable(),
  domain: z.string().min(1).max(64).nullable(),
});
export type ExtensionCatalogueItem = z.infer<typeof extensionCatalogueItemSchema>;

/** One page of the extension catalogue. A null cursor means it is the last page. */
export const extensionCatalogueSchema = z.object({
  items: z.array(extensionCatalogueItemSchema).max(100),
  nextCursor: z.string().min(1).max(1000).nullable(),
});
export type ExtensionCatalogue = z.infer<typeof extensionCatalogueSchema>;

/** The lowercase digest form every extension version and publication request carries. */
const reviewDigestSchema = z.string().regex(/^sha256:[0-9a-f]{64}$/);

/** The decisions the hub decision route accepts for a publication request. */
export const extensionReviewDecisions = ["approve", "reject"] as const;

/** The sandbox verdicts a review reads. Only `passed` lets an approval proceed. */
export const extensionReviewVerdicts = ["passed", "failed", "timed_out", "cost_capped"] as const;

/** One pending extension publication, as the operator review queue reads it. */
export const extensionReviewItemSchema = z.object({
  requestId: z.uuid(),
  projectId: z.uuid(),
  kind: z.literal("extension_publication"),
  status: z.literal("pending"),
  requestDigest: reviewDigestSchema,
  /** The revision the decision must echo back as its expected version. */
  stateVersion: z.string().regex(/^[1-9][0-9]{0,17}$/),
  evidenceRef: z.string().max(1000),
  version: z.object({
    id: z.uuid(),
    kind: z.enum(libraryItemKinds),
    name: z.string().min(1).max(120),
    version: z.string().min(1).max(80),
    digest: reviewDigestSchema,
    manifestRef: z.string().max(1000),
    state: z.string().min(1).max(40),
  }),
  requestedPermissions: z.array(z.string().min(1).max(256)).max(30),
  /** The sealed manifest, redacted by the hub. It is shown as text, never as markup. */
  sealedManifest: z.unknown().nullable(),
  scanFindings: z.array(
    z.object({
      rule: z.string().min(1).max(120),
      location: z.string().min(1).max(1000),
    }),
  ),
  evaluation: z
    .object({
      verdict: z.enum(extensionReviewVerdicts),
      reason: z.string().max(1000),
      fixtureScope: z.string().max(200),
      costMicrousd: z.number().int().min(0),
      costCapMicrousd: z.number().int().min(0),
      latencyMs: z.number().int().min(0),
      timeoutMs: z.number().int().min(0),
    })
    .nullable(),
  /** True when the caller submitted the version, so the caller cannot decide it. */
  callerIsAuthor: z.boolean(),
  /** Server-derived. The console never infers more than this flag allows. */
  canDecide: z.boolean(),
  blockedReason: z.string().max(500).nullable(),
});
export type ExtensionReviewItem = z.infer<typeof extensionReviewItemSchema>;

/** One page of pending extension publications. A null cursor means it is the last page. */
export const extensionReviewListSchema = z.object({
  items: z.array(extensionReviewItemSchema),
  nextCursor: z.string().min(1).max(1000).nullable(),
});
export type ExtensionReviewList = z.infer<typeof extensionReviewListSchema>;

/** A decision the operator submits. The digest and version are the ones the reviewer saw. */
export const extensionReviewDecisionRequestSchema = z
  .object({
    decision: z.enum(extensionReviewDecisions),
    expectedVersion: z.string().regex(/^[1-9][0-9]{0,17}$/),
    requestDigest: reviewDigestSchema,
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type ExtensionReviewDecisionRequest = z.infer<typeof extensionReviewDecisionRequestSchema>;
